import React, { useEffect, useState } from 'react';
import { GitBranch, Target, User } from 'lucide-react';
import { useUserStore } from '../../lib/stores/userStore';
import { ActivityNoteDrawer } from '../shared/activity-drawer/ActivityNoteDrawer';
import { PropRow } from '../shared/activity-drawer/ui/PropRow';
import { SearchableSelect } from '../ui/searchable-select';
import { SectionToggle } from '../shared/activity-drawer/ui/SectionToggle';
import { useDealDrawerAdapter } from './dealDrawerAdapter';

interface StatusUpdateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentStatus: string;
  dealId: string;
  dealLeadId?: string;
  dealClientId?: string;
  currentAssignee?: string;
  onStatusUpdated: () => void;
  onNotesUpdated: () => void;
  onAssigneeUpdated?: () => void;
  translations: any;
  /** Tab iniziale al mount: 'note' o 'activity'. Default: 'note'. */
  defaultTab?: 'note' | 'activity';
  /** Se presente, il drawer entra in modalità EDIT attività (pre-popola campi e fa UPDATE). */
  editingActivity?: any;
  /** Pre-fill dei campi attività (escalation dal composer inline della cronologia). */
  initialActivityData?: { title: string; type: string; description: string; due_date?: string; due_time?: string } | null;
  /** Pre-fill dell'editor nel tab Nota (escalation dal composer inline della cronologia). */
  initialNoteText?: string | null;
  /**
   * Apre lo slot "Stato offerta" già espanso quando il drawer viene mostrato dal controllo
   * di cambio stato esplicito (badge stato in `DealClientInfo` → `onStatusUpdate`, Task 7
   * redesign, eccezione dichiarata 2). Nessun effetto con `dealId` vuoto (nessuno slot).
   */
  defaultStatusOpen?: boolean;
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Bozza', color: '#9B9A97' },
  { value: 'sent', label: 'Inviata', color: '#2383E2' },
  { value: 'accepted', label: 'Accettata', color: '#0F7B6C' },
  { value: 'rejected', label: 'Rifiutata', color: '#D44C47' },
];

function StatusDot({ color }: { color?: string }) {
  return <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color || '#9B9A97' }} />;
}

/**
 * Drawer Nota/Attività di un'offerta: thin wrapper sul drawer condiviso
 * (`shared/activity-drawer/ActivityNoteDrawer`). Qui vive SOLO ciò che è specifico
 * dell'entità offerta — lo slot "Stato offerta" (stato + assegnatario) e l'adapter con
 * le sue scritture (`dealDrawerAdapter.ts`). Editor, tab, date, tipi attività custom,
 * calendari Google/Microsoft, attendee, Meet/Teams e AlfaDocs sono TUTTI nella shell.
 *
 * Props pubbliche invariate salvo l'aggiunta di `defaultStatusOpen` (Task 7 redesign — solo
 * presentazione): `DealDetailsPage`, `ProjectDetailsPage` e `SalesActivitiesTab` non
 * cambiano import né props esistenti. Con `dealId` vuoto (SalesActivitiesTab) il drawer è in
 * EDIT-ONLY: lo slot di stato non viene renderizzato e l'adapter non scrive sull'offerta.
 *
 * Redesign T7: lo slot è un `SectionToggle` ("Stato offerta", chiuso di default, sunto con
 * pallino colore) con dentro `PropRow` per stato/assegnatario. Fonte pre-redesign: versione
 * precedente di questo file, :98-160.
 */
const StatusUpdateDrawer: React.FC<StatusUpdateDrawerProps> = ({
  isOpen,
  onClose,
  currentStatus,
  dealId,
  dealLeadId,
  dealClientId,
  currentAssignee,
  onStatusUpdated,
  onNotesUpdated,
  onAssigneeUpdated,
  translations: t,
  defaultTab = 'note',
  editingActivity,
  initialActivityData,
  initialNoteText,
  defaultStatusOpen = false,
}) => {
  const [newStatus, setNewStatus] = useState(currentStatus);
  const [newAssignee, setNewAssignee] = useState(currentAssignee || '');
  const [statusOpen, setStatusOpen] = useState(defaultStatusOpen);

  const { users, fetchUsers } = useUserStore();

  // Fonte :219-223 — la select assegnatario dello slot ha bisogno degli utenti dell'org.
  useEffect(() => {
    if (isOpen) fetchUsers(false);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Allineamento alle props + reset all'apertura (fonte :226-231).
  useEffect(() => setNewStatus(currentStatus), [currentStatus]);
  useEffect(() => setNewAssignee(currentAssignee || ''), [currentAssignee]);
  useEffect(() => {
    if (!isOpen) return;
    setNewStatus(currentStatus);
    setNewAssignee(currentAssignee || '');
    setStatusOpen(defaultStatusOpen);
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const { adapter, handleNotesUpdated } = useDealDrawerAdapter({
    isOpen,
    dealId,
    dealLeadId,
    dealClientId,
    currentStatus,
    currentAssignee,
    newStatus,
    newAssignee,
    onStatusUpdated,
    onNotesUpdated,
    onAssigneeUpdated,
  });

  const currentStatusOpt = STATUS_OPTIONS.find((s) => s.value === currentStatus);
  const newStatusOpt = STATUS_OPTIONS.find((s) => s.value === newStatus);
  const statusModified = newStatus !== currentStatus || newAssignee !== (currentAssignee || '');

  // Slot "Stato offerta": solo se c'è un'offerta di contesto (fonte :98, M9 invariato — con
  // `dealId` vuoto nessuno slot).
  const statusSection = dealId ? (
    <SectionToggle
      open={statusOpen}
      onToggle={() => setStatusOpen((prev) => !prev)}
      icon={<Target className="h-3 w-3 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />}
      title="Stato offerta"
      dataTour="drawer-status-toggle"
      summary={
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#37352F] dark:text-white">
          <StatusDot color={currentStatusOpt?.color} />
          {currentStatusOpt?.label || currentStatus}
        </span>
      }
      trailing={
        statusModified ? (
          <span className="text-[11px] font-medium text-[#2383E2]">Modificato</span>
        ) : (
          <span className="text-[11px] font-medium text-[#787774] dark:text-gray-400">Modifica</span>
        )
      }
    >
      <PropRow icon={<GitBranch className="h-3 w-3" strokeWidth={1.5} />} label="Nuovo stato" wrap>
        <StatusDot color={newStatusOpt?.color} />
        <SearchableSelect
          value={newStatus}
          onValueChange={(value) => setNewStatus(value)}
          options={STATUS_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
          placeholder="Seleziona stato..."
          ariaLabel="Nuovo stato"
          className="flex-1 min-w-0"
          triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
        />
      </PropRow>

      <PropRow icon={<User className="h-3 w-3" strokeWidth={1.5} />} label="Assegnatario offerta" wrap>
        <SearchableSelect
          value={newAssignee}
          onValueChange={(value) => setNewAssignee(value)}
          options={[
            { value: '', label: 'Non assegnata' },
            ...users
              .filter((u) => !(u as any).is_super_admin)
              .map((u) => ({ value: u.id, label: u.full_name || u.email || u.id })),
          ]}
          placeholder="Non assegnata"
          ariaLabel="Assegnatario offerta"
          className="flex-1 min-w-0"
          triggerClassName="h-auto py-0 px-0 border-none bg-transparent shadow-none ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 justify-between text-[13px]"
        />
      </PropRow>

      <div className="text-[11px] text-[rgba(55,53,47,0.45)] dark:text-white/40">
        Le modifiche vengono salvate insieme alla nota o all'attività.
      </div>
    </SectionToggle>
  ) : undefined;

  return (
    <ActivityNoteDrawer
      isOpen={isOpen}
      onClose={onClose}
      adapter={adapter}
      defaultTab={defaultTab}
      editingActivity={editingActivity}
      initialActivityData={initialActivityData}
      initialNoteText={initialNoteText}
      statusSection={statusSection}
      translations={t}
      onNotesUpdated={handleNotesUpdated}
    />
  );
};

export default StatusUpdateDrawer;
